/**
 * La cabecera común: marca, idioma, cuenta y salida.
 *
 * Existía solo dentro de la portada, así que fuera de ella —asignaturas, editor, partidas,
 * analíticas— no había cabecera ninguna, y `signOut` no se podía invocar desde ningún otro sitio.
 * Quien se metía en el editor tenía que volver a la raíz para cambiar de idioma o para salir.
 *
 * Incluye el enlace de salto al contenido, que estaba traducido en los tres idiomas, con los
 * quince `<main id="contenido">` ya puestos, y no lo pintaba nadie.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { SessionProfile } from '@clica/shared';
import { useSession } from '../../auth/session.js';
import { SelectorIdioma } from '../selector-idioma.js';
import { Boton } from './boton.js';
import { Marca } from './marca.js';
import { Rotulo } from './rotulo.js';

export interface MarcoProps {
  readonly profile: SessionProfile;
  /** Migas de pan, de la más general a la más concreta. La última es dónde se está. */
  readonly migas?: readonly { readonly texto: string; readonly a?: string }[];
  /**
   * Cabecera reducida a la marca y a la salida.
   *
   * La usan las pantallas de PARTIDA —`/join` y `/play`—, donde cada fila de cabecera sale del
   * sitio de la pregunta, que es lo único que hay que mirar. Se quedan la marca —que es el enlace
   * de vuelta—, el rol y el idioma; se van la cuenta y la salida, que no son lo que nadie necesita
   * a mitad de partida.
   *
   * El panel del alumnado NO la usa: ahí sí hay sitio, y es el único lugar desde el que se cierra
   * la sesión.
   */
  readonly minima?: boolean;
  readonly children: React.ReactNode;
}

export function Marco({
  profile,
  migas = [],
  minima = false,
  children,
}: MarcoProps): React.JSX.Element {
  const { t } = useTranslation();
  const signOut = useSession((store) => store.signOut);

  return (
    <>
      <a href="#contenido" className="saltar-al-contenido">
        {t('common.skipToContent')}
      </a>

      <header className="border-line border-b-2">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 p-4">
          {/* `min-h-11`: es un enlace de verdad —lleva al principio— y como tal necesita los
              44 px de área táctil de WCAG 2.5.5. */}
          <Link to="/" className="rounded-chip flex min-h-11 items-center gap-3">
            <Marca size={32} className="text-edge" />
            <span className="font-display text-h3 font-extrabold">{t('app.name')}</span>
            <Rotulo>{t(`roles.${profile.role}`)}</Rotulo>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            {/* El idioma va SIEMPRE en la cabecera, también en la versión mínima.
                Estaba al pie de la pantalla del alumnado, debajo del campo del PIN, que es donde
                nadie lo busca y donde ocupa sitio en la única pantalla que solo tiene que hacer
                una cosa. Cabía en la cabecera desde que dejó de ser una lista de tres nombres
                escritos enteros. */}
            <SelectorIdioma />
            {/*
             * En la versión mínima no van ni la cuenta ni la salida.
             *
             * No es esconderlas: es que en un móvil de 390 px la cabecera completa ocupa dos
             * filas, y esas dos filas salen del sitio de la pregunta, que es lo único que hay que
             * mirar durante una partida. Salir de la sesión a mitad de partida no es lo que nadie
             * quiere hacer; volver, sí, y para eso está la marca, que es un enlace al principio.
             */}
            {!minima && (
              <>
                <Link
                  to="/cuenta"
                  className="text-micro font-display rounded-chip text-text-dim flex min-h-11 items-center px-3 font-bold uppercase underline underline-offset-4"
                >
                  {t('account.title')}
                </Link>
                <Boton variante="fantasma" onClick={() => void signOut()}>
                  {t('auth.signOut')}
                </Boton>
              </>
            )}
          </div>
        </div>

        {migas.length > 0 && (
          <nav aria-label={t('common.breadcrumb')} className="mx-auto max-w-5xl px-4 pb-3">
            <ol className="flex flex-wrap items-center gap-2">
              {migas.map((miga, indice) => (
                <li key={`${miga.texto}-${indice}`} className="flex items-center gap-2">
                  {indice > 0 && (
                    <span aria-hidden="true" className="text-text-dim">
                      /
                    </span>
                  )}
                  {miga.a === undefined ? (
                    // La última no es enlace: ya se está ahí, y un enlace a la página actual es
                    // una promesa que no lleva a ninguna parte.
                    <Rotulo aria-current="page">{miga.texto}</Rotulo>
                  ) : (
                    <Link
                      to={miga.a}
                      className="text-micro font-display rounded-chip flex min-h-11 items-center font-bold uppercase underline underline-offset-4"
                    >
                      {miga.texto}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
      </header>

      {children}
    </>
  );
}
